import React, { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { AuthContext } from "../App";
import CartDropdown from "../components/CartDropdown";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from "../components/ui/dropdown-menu";
import { 
  Flower2, 
  Plus, 
  LogOut, 
  Trash2, 
  Eye,
  Palette,
  ChevronDown,
  ShoppingBag,
  Download,
  Package,
  Loader2,
  FileText,
  CheckCircle2,
  ShoppingCart,
  Sparkles,
  User
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper to normalize color_palette to array format (handles both old and new data structures)
const getColorPaletteArray = (colorPalette) => {
  if (!colorPalette) return [];
  // New format: object with 'colors' array
  if (colorPalette.colors && Array.isArray(colorPalette.colors)) {
    return colorPalette.colors;
  }
  // Old format: direct array
  if (Array.isArray(colorPalette)) {
    return colorPalette;
  }
  return [];
};

// Helper to normalize brand_values to array of display strings
const getBrandValuesArray = (brandValues) => {
  if (!brandValues) return [];
  if (Array.isArray(brandValues)) {
    // New format: array of objects with 'value' key
    if (brandValues.length > 0 && typeof brandValues[0] === 'object' && brandValues[0].value) {
      return brandValues.map(v => v.value);
    }
    // Old format: array of strings
    return brandValues;
  }
  return [];
};

const Dashboard = () => {
  const navigate = useNavigate();
  const { user, setUser } = useContext(AuthContext);
  const [brands, setBrands] = useState([]);
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isPurchasesLoading, setIsPurchasesLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [activeTab, setActiveTab] = useState("brands");

  useEffect(() => {
    fetchBrands();
    fetchPurchases();
  }, []);

  const fetchBrands = async () => {
    try {
      const response = await axios.get(`${API}/brands`);
      setBrands(response.data);
    } catch (error) {
      console.error("Error fetching brands:", error);
      toast.error("Failed to load brands");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPurchases = async () => {
    try {
      const response = await axios.get(`${API}/store/purchases`);
      const purchasesData = response.data.purchases || [];
      
      setPurchases(purchasesData);
    } catch (error) {
      console.error("Error fetching purchases:", error);
    } finally {
      setIsPurchasesLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
      setUser(null);
      navigate("/", { replace: true });
    } catch (error) {
      console.error("Logout error:", error);
      navigate("/", { replace: true });
    }
  };

  const handleDeleteBrand = async (brandId) => {
    if (!window.confirm("Are you sure you want to delete this brand?")) return;
    
    try {
      await axios.delete(`${API}/brands/${brandId}`);
      setBrands(brands.filter(b => b.brand_id !== brandId));
      toast.success("Brand deleted successfully");
    } catch (error) {
      console.error("Delete error:", error);
      toast.error("Failed to delete brand");
    }
  };

  const handleDownloadPDF = async (purchaseId, packageName) => {
    setDownloadingId(purchaseId);
    try {
      const response = await axios.get(
        `${API}/store/download-pdf/${purchaseId}`,
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${packageName || 'brand'}_guide.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("PDF downloaded successfully!");
    } catch (error) {
      console.error("Download error:", error);
      toast.error("Failed to download PDF");
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      {/* Navigation */}
      <nav className="nav-glass sticky top-0 z-50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div className="flex items-center cursor-pointer" onClick={() => navigate("/")}>
              <span className="text-xl font-semibold text-[#2C332E]" style={{ fontFamily: "'Playfair Display', serif" }}>
                Bloomwright Studio
              </span>
            </div>
            <div className="hidden md:flex items-center gap-2">
              <Button 
                variant="ghost" 
                className="text-[#C77D63] font-semibold"
              >
                Dashboard
              </Button>
              <Button 
                variant="ghost" 
                onClick={() => navigate("/store")}
                className="text-[#78716C] hover:text-[#2C332E]"
                data-testid="nav-store-btn"
              >
                Store
              </Button>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {/* Cart Dropdown */}
            <CartDropdown />
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 hover:bg-[#F5F0EB]" data-testid="user-menu-btn">
                  <div className="w-8 h-8 rounded-full bg-[#8DA399]/20 flex items-center justify-center">
                    <User className="w-4 h-4 text-[#8DA399]" />
                  </div>
                  <span className="text-[#2C332E] hidden sm:inline">{user?.name}</span>
                  <ChevronDown className="w-4 h-4 text-[#78716C]" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem 
                  onClick={handleLogout}
                  className="text-red-600 cursor-pointer"
                  data-testid="logout-btn"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Sign Out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-[#C77D63] font-accent text-xl mb-2">Welcome back,</p>
            <h1 className="text-4xl md:text-5xl tracking-tight text-[#2C332E]">
              {user?.name?.split(" ")[0] || "Creator"}
            </h1>
          </div>
          <Button 
            onClick={() => navigate("/questionnaire")}
            className="bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full px-6 py-3 hover-lift flex items-center gap-2"
            data-testid="create-brand-btn"
          >
            <Plus className="w-5 h-5" />
            Create New Brand
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-[#F5F0EB] mb-8 flex-wrap">
            <TabsTrigger 
              value="brands" 
              className="data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <Palette className="w-4 h-4 mr-2" />
              My Brands ({brands.length})
            </TabsTrigger>
            <TabsTrigger 
              value="purchases" 
              className="data-[state=active]:bg-white data-[state=active]:text-[#2C332E]"
            >
              <Package className="w-4 h-4 mr-2" />
              My Purchases ({purchases.length})
            </TabsTrigger>
          </TabsList>

          {/* Brands Tab */}
          <TabsContent value="brands">
            {isLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="spinner"></div>
              </div>
            ) : brands.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 rounded-full bg-[#F5F0EB] flex items-center justify-center mx-auto mb-6">
                  <Palette className="w-10 h-10 text-[#8DA399]" />
                </div>
                <h2 className="text-2xl font-semibold text-[#2C332E] mb-3">No brands yet</h2>
                <p className="text-[#78716C] mb-6 max-w-md mx-auto">
                  Start your brand journey by answering a few questions about your business vision.
                </p>
                <Button 
                  onClick={() => navigate("/questionnaire")}
                  className="bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full px-8 py-3 hover-lift"
                  data-testid="empty-create-brand-btn"
                >
                  Create New Brand
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {brands.map((brand) => (
                  <Card 
                    key={brand.brand_id} 
                    className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft hover:shadow-hover transition-shadow duration-300 overflow-hidden"
                    data-testid={`brand-card-${brand.brand_id}`}
                  >
                    <div className="h-3 flex">
                      {getColorPaletteArray(brand.color_palette).slice(0, 5).map((color, index) => (
                        <div 
                          key={index}
                          className="flex-1"
                          style={{ backgroundColor: color.hex || color }}
                        />
                      ))}
                    </div>
                    
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="text-xl text-[#2C332E]">{brand.brand_name}</CardTitle>
                          <p className="text-sm text-[#78716C] mt-1">{brand.business_type?.replace(/_/g, ' ')}</p>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <p className="text-[#78716C] italic mb-4">&ldquo;{brand.tagline}&rdquo;</p>
                      
                      <div className="flex flex-wrap gap-2 mb-4">
                        {getBrandValuesArray(brand.brand_values).slice(0, 3).map((value, index) => (
                          <span 
                            key={index}
                            className="px-3 py-1 bg-[#F5F0EB] text-[#2C332E] text-xs rounded-full"
                          >
                            {value}
                          </span>
                        ))}
                      </div>

                      {/* Activation Status & Button */}
                      {!brand.activated ? (
                        <div className="mb-4 p-3 bg-gradient-to-r from-[#C77D63]/10 to-[#8DA399]/10 rounded-xl">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Sparkles className="w-4 h-4 text-[#C77D63]" />
                              <span className="text-sm font-medium text-[#2C332E]">Ready to Activate</span>
                            </div>
                            <span className="text-[#C77D63] font-bold">$47</span>
                          </div>
                          <Button 
                            onClick={() => navigate(`/activate-brand/${brand.brand_id}`)}
                            className="w-full mt-3 bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full"
                            data-testid={`activate-brand-${brand.brand_id}`}
                          >
                            <ShoppingCart className="w-4 h-4 mr-2" />
                            Activate Brand
                          </Button>
                        </div>
                      ) : (
                        <div className="mb-4 p-3 bg-[#8DA399]/10 rounded-xl">
                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-4 h-4 text-[#8DA399]" />
                            <span className="text-sm font-medium text-[#8DA399]">Brand Activated</span>
                          </div>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2 pt-4 border-t border-[#E6E1DB]">
                        <Button 
                          variant="ghost"
                          onClick={() => navigate(`/brand/${brand.brand_id}`)}
                          className="flex-1 text-[#8DA399] hover:bg-[#8DA399]/10"
                          data-testid={`view-brand-${brand.brand_id}`}
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View
                        </Button>
                        <Button 
                          variant="ghost"
                          onClick={() => handleDeleteBrand(brand.brand_id)}
                          className="text-red-500 hover:bg-red-50"
                          data-testid={`delete-brand-${brand.brand_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Purchases Tab */}
          <TabsContent value="purchases">
            {isPurchasesLoading ? (
              <div className="flex items-center justify-center py-20">
                <div className="spinner"></div>
              </div>
            ) : purchases.length === 0 ? (
              <div className="text-center py-20">
                <div className="w-20 h-20 rounded-full bg-[#F5F0EB] flex items-center justify-center mx-auto mb-6">
                  <Package className="w-10 h-10 text-[#8DA399]" />
                </div>
                <h2 className="text-2xl font-semibold text-[#2C332E] mb-3">No purchases yet</h2>
                <p className="text-[#78716C] mb-6 max-w-md mx-auto">
                  Browse our ready-made brand packages and start your business today.
                </p>
                <Button 
                  onClick={() => navigate("/store")}
                  className="bg-[#8DA399] hover:bg-[#7A8F86] text-white rounded-full px-8 py-3 hover-lift"
                >
                  <ShoppingBag className="w-4 h-4 mr-2" />
                  Visit Store
                </Button>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {purchases.map((purchase) => (
                  <Card 
                    key={purchase.purchase_id} 
                    className="bg-white rounded-2xl border-[#E6E1DB]/50 shadow-soft hover:shadow-hover transition-shadow duration-300"
                    data-testid={`purchase-card-${purchase.purchase_id}`}
                  >
                    <CardHeader className="pb-2">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#C77D63]/10 flex items-center justify-center">
                            <Package className="w-5 h-5 text-[#C77D63]" />
                          </div>
                          <div>
                            <CardTitle className="text-lg text-[#2C332E]">{purchase.package_name}</CardTitle>
                            <p className="text-sm text-[#78716C]">
                              {new Date(purchase.purchased_at).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </CardHeader>
                    
                    <CardContent>
                      <div className="flex items-center justify-between py-3 border-b border-[#E6E1DB]">
                        <span className="text-[#78716C]">Amount Paid</span>
                        <span className="font-semibold text-[#2C332E]">${purchase.amount?.toFixed(2)}</span>
                      </div>
                      
                      {purchase.add_ons && purchase.add_ons.length > 0 && (
                        <div className="py-3 border-b border-[#E6E1DB]">
                          <p className="text-sm text-[#78716C] mb-2">Add-ons included:</p>
                          <div className="flex flex-wrap gap-1">
                            {purchase.add_ons.map((addOn, index) => (
                              <span 
                                key={index}
                                className="px-2 py-0.5 bg-[#F5F0EB] text-[#2C332E] text-xs rounded-full"
                              >
                                {addOn.replace(/_/g, ' ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="pt-4">
                        <Button 
                          onClick={() => handleDownloadPDF(purchase.purchase_id, purchase.package_name)}
                          disabled={downloadingId === purchase.purchase_id}
                          className="w-full bg-[#C77D63] hover:bg-[#B06A52] text-white rounded-full"
                          data-testid={`download-pdf-${purchase.purchase_id}`}
                        >
                          {downloadingId === purchase.purchase_id ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Downloading...
                            </>
                          ) : (
                            <>
                              <Download className="w-4 h-4 mr-2" />
                              Download PDF Guide
                            </>
                          )}
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default Dashboard;
